import React from 'react';
import { useAppSelector } from '../app/hooks';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@material-ui/core';
import getThemeLookup from '../selectors/getThemeLookup';
import { useFormikContext } from 'formik';
import { TEXT_PROPERTIES } from '../utils/theme';
import combineStyles from '../utils/combineStyles';

const FormTextField = ({
  id,
  name,
  type,
  label,
  required = false,
  onChange,
  handleBlur,
  disableStyles = false,
  disabled,
  InputProps = {},
  multiline = false,
  autoFocus = false,
  shrinkLabel = true,
  inputRef,
  defaultValue = '',
}: {
  id?: string;
  name: string;
  type: string;
  required?: boolean;
  label: string;
  onChange: (e: any) => void;
  handleBlur?: () => void;
  disableStyles?: boolean;
  disabled?: boolean;
  InputProps?: any;
  multiline?: boolean;
  autoFocus?: boolean;
  shrinkLabel?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  defaultValue?: string;
}) => {
  const p = useAppSelector(getThemeLookup);

  const sharedInputProps = {
    InputProps: {
      style: {
        //textfield text filled colors
        color: p('textColor', ['color']).color,
        ...p('formTextFieldInput', TEXT_PROPERTIES),
      },
    },
    InputLabelProps: {
      // style: {
      //   //textfield label - placeholder colors
      //   color: p('labelColor', ['color']).color,
      //   font: p('labelColor', ['font']).font,
      // },
      style: combineStyles(p('defaultText', TEXT_PROPERTIES), p('formLabel', TEXT_PROPERTIES)),
    },
  };

  const useStyles = makeStyles(() => ({
    formControlLabel: {
      color: p('checkboxLabelColor', ['color']).color,
    },
    caretIcon: {
      color: `${p('caretColor', ['color']).color} !important`,
    },
    dropdown: {
      '& .MuiSelect-select': {
        //selected menu item color
        color: p('textColor', ['color']).color,
      },
    },
    datePickerActive: {
      '& .MuiInputBase-root': {
        color: p('textColor', ['color']).color,
      },
    },
    datePickerEmpty: {
      '& .MuiInputBase-root': {
        color: p('materialDatePickerPlaceholderColor', ['color']).color,
      },
    },
    datePickerErrors: {
      '& .MuiInputBase-root': {
        color: p('textColor', ['color']).color,
      },
    },
    errors: {
      '& .MuiOutlinedInput-root': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${p('materialTextFieldErrorBorder', ['color']).color} !important`,
        },
      },
    },
    root: {
      '& ::-webkit-calendar-picker-indicator': {
        filter: `invert(${p('fieldIcons', ['color']).color})`,
      },
      //label - placeholder background color
      '& .MuiFormLabel-root': {
        backgroundColor: p('labelBackgroundColor', ['color']).color,
        borderRadius: 5,
        paddingLeft: 5,
        paddingRight: 5,
      },

      //text field errors
      '&  .MuiFormHelperText-root.Mui-error': {
        backgroundColor: p('helperBackground', ['color']).color,
        color: p('helperColor', ['color']).color,
      },
      // focused color for textfield with variant='outlined'
      '& .MuiOutlinedInput-root': {
        //textfield background color
        background: p('fieldBackground', ['color']).color,

        //text field border unfocused
        '& fieldset': {
          borderColor: p('fieldDefaultBorder', ['color']).color,
        },
        //text field border focused
        '&.Mui-focused fieldset': {
          borderColor: p('fieldBorderFocus', ['color']).color,
        },
        //text field border hover
        '&:hover fieldset': {
          borderColor: p('fieldHoverBorderColor', ['color']).color,
        },
        // text field border hover when focussed should not change the color of the border
        '&.Mui-focused:hover fieldset': {
          borderColor: p('fieldBorderFocus', ['color']).color,
        },
      },
      // label color when unfocused
      '& .MuiInputLabel-root': {
        color: `${p('fieldDefaultBorder', ['color']).color} !important`,
      },
      // label color when focused
      '& .MuiInputLabel-root.Mui-focused': {
        color: `${p('fieldDefaultBorder', ['color']).color} !important`,
      },
      //checkbox
      '&$checked': {
        color: p('checkboxCheckedColor', ['color']).color,
      },
    },

    checked: {},
  }));

  const muiStyles = useStyles();
  const formik = useFormikContext<FormikFields>();

  const { errors, setFieldTouched, touched, values } = formik;

  return (
    <TextField
      multiline={multiline}
      minRows={multiline ? 4 : 1}
      size="small"
      variant="outlined"
      value={values[name as keyof FormikFields] || defaultValue}
      fullWidth
      id={id ? id : name}
      name={name}
      type={type}
      label={label}
      disabled={disabled ? disabled : false}
      onChange={onChange}
      required={required}
      style={disableStyles ? {} : styles.inputField}
      error={touched[name as keyof FormikFields] && Boolean(errors[name as keyof FormikFields])}
      helperText={touched[name as keyof FormikFields] ? errors[name as keyof FormikFields] : ''}
      // TODO: These are deprecated use slot props:
      InputProps={{ ...sharedInputProps.InputProps, ...InputProps }}
      InputLabelProps={{
        shrink: shrinkLabel ? shrinkLabel : undefined,
        ...sharedInputProps.InputLabelProps,
      }}
      //^

      className={
        touched[name as keyof FormikFields] && errors[name as keyof FormikFields]
          ? muiStyles.root + ' ' + muiStyles.errors
          : muiStyles.root
      }
      onBlur={() => {
        setFieldTouched(name);
        if (handleBlur) handleBlur();
      }}
      autoFocus={autoFocus}
      inputRef={inputRef}
    />
  );
};

export default FormTextField;

const styles: Styles = {
  inputField: {
    marginBottom: 10,
    marginTop: 10,
  },
};
